import { create } from 'zustand';
import { useCallback } from 'react';

export interface ErrorDialogState {
  // State
  errorMsg: string;
  isOpen: boolean;

  // Actions
  showError: (errorMsg: string) => void;
  hideError: () => void;
}

interface ErrorDialogAction {
  type: string;
  errorMsg?: string;
}

export const useErrorDialogStore = create<ErrorDialogState>()(set => ({
  errorMsg: '',
  isOpen: false,

  showError: errorMsg =>
    set({
      errorMsg,
      isOpen: true,
    }),

  hideError: () =>
    set({
      errorMsg: '',
      isOpen: false,
    }),
}));

// Convenience selectors
export const useErrorMsg = () => useErrorDialogStore(state => state.errorMsg);
export const useErrorDialogOpen = () =>
  useErrorDialogStore(state => state.isOpen);

// Individual action hooks to avoid reference issues
export const useShowError = () => useErrorDialogStore(state => state.showError);
export const useHideError = () => useErrorDialogStore(state => state.hideError);

// Legacy hook for backward compatibility with existing ErrorDialogDispatch usage
export const useErrorDialogDispatch = () => {
  const showError = useShowError();
  const hideError = useHideError();

  return useCallback(
    (action: ErrorDialogAction) => {
      switch (action.type) {
        case 'OPEN_ERROR_DIALOG':
          if (action.errorMsg) {
            showError(action.errorMsg);
          } else {
            hideError();
          }
          break;
        default:
          console.warn(`Unknown error dialog action type: ${action.type}`);
      }
    },
    [showError, hideError],
  );
};
